import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformStatus } from '@/contexts/PlatformStatusContext';
import { supabase } from '@/integrations/supabase/client';
import WaitingList from '@/pages/WaitingList';

/**
 * When `coming_soon_mode` is true in admin_settings, render the WaitingList
 * page for everyone EXCEPT authenticated admins. Admins always see the real
 * route. Auth, reset-password, and policy pages are NOT wrapped (handled by
 * route ordering in App.tsx).
 */
export function ComingSoonGuard({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { loading: statusLoading } = usePlatformStatus();
  const [comingSoon, setComingSoon] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const location = useLocation();

  // Read coming_soon_mode flag (and listen for changes)
  useEffect(() => {
    let active = true;
    const fetchFlag = async () => {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('value')
        .eq('key', 'coming_soon_mode')
        .maybeSingle();
      if (!active) return;
      // Fail-closed: if we can't read the flag, assume takeover is ON so the
      // live site is never accidentally revealed on a network/RLS failure.
      if (error) {
        console.error('[ComingSoonGuard] settings fetch failed, defaulting to takeover ON', error);
        setComingSoon(true);
        return;
      }
      setComingSoon(data?.value === 'true');
    };
    fetchFlag();

    const channel = supabase
      .channel('coming-soon-mode')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'admin_settings', filter: 'key=eq.coming_soon_mode' },
        (payload: any) => {
          const row = payload.new;
          if (row && active) setComingSoon(row.value === 'true');
        }
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, []);

  // Check admin role once we know the user
  useEffect(() => {
    let active = true;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (active) setIsAdmin(!!data);
    })();
    return () => { active = false; };
  }, [user]);

  // Loading: render nothing briefly to avoid flicker
  if (comingSoon === null || authLoading || statusLoading || (user && isAdmin === null)) {
    return <div className="min-h-screen bg-background" />;
  }

  // Takeover off → normal site
  if (!comingSoon) return <>{children}</>;

  // Admins bypass takeover everywhere
  if (user && isAdmin) return <>{children}</>;

  // Allow the admin's own /admin route to load behind the guard if they manage
  // to navigate there before login (will hit ProtectedRoute next).
  if (location.pathname.startsWith('/admin')) return <>{children}</>;

  return <WaitingList />;
}
